import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgSrc } from "../../lib/api";
import { StoneForm } from "./StoneForm";
import { DICT } from "../../i18n/dict";

const it = DICT.it;

export const StonesAdmin = () => {
  const [stones, setStones] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([api.get("/admin/stones"), api.get("/meta")]);
      setStones(Array.isArray(s.data) ? s.data : []);
      setCats(m.data?.categories || []);
    } catch (e) { toast.error(errMsg(e, "Errore di caricamento")); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (s, changes) => {
    try {
      await api.put(`/admin/stones/${s.id}`, { ...s, ...changes });
      toast.success("Aggiornato");
      load();
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare definitivamente ${s.code}? Per mantenerne lo storico usa “Archivia”.`)) return;
    await api.delete(`/admin/stones/${s.id}`);
    toast.success("Eliminata");
    load();
  };

  const list = stones.filter((s) => !q || `${s.code} ${s.name_en} ${s.name_it}`.toLowerCase().includes(q.toLowerCase()));
  const catName = (slug) => cats.find((c) => c.slug === slug)?.name_it || slug;

  return (
    <section data-testid="admin-stones">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-sm text-[#555555]">{stones.length} schede · {stones.filter((s) => !s.archived && s.availability === "available").length} disponibili online</p>
          <input className="field !py-2 text-sm max-w-xs mt-2" placeholder="Cerca codice o nome" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-stone-search" />
        </div>
        <button type="button" onClick={() => setEditing({})} className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FFFFFF] px-5 min-h-[44px] text-xs tracking-[0.14em] uppercase" data-testid="admin-new-stone">
          <Plus className="w-4 h-4" strokeWidth={1.5} />Nuova lastra / blocco
        </button>
      </div>
      <div className="overflow-x-auto border-t border-[#1A1A1A]/12">
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className="text-left eyebrow !text-[0.6rem] text-[#555555]">
            <th className="py-3 pr-4 font-normal">Foto</th><th className="pr-4 font-normal">Codice / Nome</th><th className="pr-4 font-normal">Tipo</th>
            <th className="pr-4 font-normal">Disponibilità</th><th className="pr-4 font-normal">In home</th><th className="pr-4 font-normal">Visibile</th><th className="font-normal text-right">Azioni</th>
          </tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className={`border-t border-[#1A1A1A]/10 ${s.archived ? "opacity-50" : ""}`} data-testid={`admin-stone-row-${s.code}`}>
                <td className="py-3 pr-4"><img src={imgSrc(s.images?.[0])} alt="" className="w-12 h-14 object-cover bg-[#F0F0F0]" /></td>
                <td className="pr-4"><p className="f-mono text-xs">{s.code} {s.is_demo && <span className="ml-2 bg-[#1A1A1A] text-[#FFFFFF] px-1.5 py-0.5 text-[0.6rem]">DEMO</span>}</p><p className="mt-1">{s.name_it || s.name_en}</p></td>
                <td className="pr-4">{catName(s.category)} · {it.form[s.form]}</td>
                <td className="pr-4">
                  <select className="field !py-1 text-sm w-40" value={s.availability} onChange={(e) => patch(s, { availability: e.target.value })} data-testid={`admin-avail-${s.code}`}>
                    {["available", "reserved", "sold"].map((a) => <option key={a} value={a}>{it.avail[a]}</option>)}
                  </select>
                </td>
                <td className="pr-4"><input type="checkbox" className="accent-[#1A1A1A] w-4 h-4" checked={!!s.featured} onChange={(e) => patch(s, { featured: e.target.checked })} data-testid={`admin-featured-${s.code}`} /></td>
                <td className="pr-4"><button type="button" className="text-xs underline underline-offset-2" onClick={() => patch(s, { archived: !s.archived })} data-testid={`admin-archive-${s.code}`}>{s.archived ? "Archiviata — ripristina" : "Archivia"}</button></td>
                <td className="text-right whitespace-nowrap">
                  <a href={`/it/stones/${s.code}`} target="_blank" rel="noreferrer" className="inline-block p-2" aria-label="Apri"><ExternalLink className="w-4 h-4" strokeWidth={1.25} /></a>
                  <button type="button" onClick={() => setEditing(s)} className="p-2" aria-label="Modifica" data-testid={`admin-edit-${s.code}`}><Pencil className="w-4 h-4" strokeWidth={1.25} /></button>
                  <button type="button" onClick={() => remove(s)} className="p-2 text-[#8a3b2e]" aria-label="Elimina" data-testid={`admin-delete-${s.code}`}><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <StoneForm stone={editing} cats={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </section>
  );
};
